import { useState } from "react";
import { CalendarClock, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { fmtDateTime, isoDate, todayISO, toLocalValue } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const OPEN = ["new", "assigned", "in_progress", "waiting_client", "waiting_internal", "scheduled"];

/** "overdue" (time passed), "today" (later today in Melbourne) or "upcoming". */
export function followUpTone(at) {
  if (!at) return "upcoming";
  if (new Date(at) < new Date()) return "overdue";
  return isoDate(at) === todayISO() ? "today" : "upcoming";
}

const LINE = { overdue: "text-red-700 font-semibold", today: "text-amber-700 font-semibold", upcoming: "text-sky-800" };
const BOX = {
  overdue: "border-red-200 bg-red-50 text-red-900",
  today: "border-amber-200 bg-amber-50 text-amber-900",
  upcoming: "border-sky-200 bg-sky-50 text-sky-900",
};
const SUFFIX = { overdue: " · overdue", today: " · due today", upcoming: "" };

const hasNext = (enq) => OPEN.includes(enq.status || "new") && (enq.next_follow_up_at || enq.next_action);

/** One line under an enquiry in the list: "Next: Call back · 30-Oct-2026 9:00 am". */
export const NextActionLine = ({ enq }) => {
  if (!hasNext(enq)) return null;
  const tone = followUpTone(enq.next_follow_up_at);
  return (
    <div className={cn("mt-1 flex items-center gap-1 text-2xs", LINE[tone])} data-testid={`enquiry-row-next-${enq.id}`}>
      <CalendarClock className="h-3 w-3 shrink-0" />
      <span className="truncate">Next: {enq.next_action || "Follow up"}{enq.next_follow_up_at ? ` · ${fmtDateTime(enq.next_follow_up_at)}` : ""}{SUFFIX[tone]}</span>
    </div>
  );
};

const tomorrowAt9 = () => {
  const d = new Date(Date.now() + 86400000);
  return `${isoDate(d)}T09:00`;
};
const QUICK = [
  { label: "Tomorrow 9am", days: 1 }, { label: "In 3 days", days: 3 }, { label: "Next week", days: 7 },
];

const NextActionForm = ({ enq, onCancel, onSaved }) => {
  const [text, setText] = useState(enq.next_action || "");
  const [at, setAt] = useState(toLocalValue(enq.next_follow_up_at) || tomorrowAt9());
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/enquiries/${enq.id}/next-action`, { next_action: text || null, next_action_at: at });
      toast.success("Next action saved");
      onSaved();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-zinc-300 bg-white p-3 space-y-2" data-testid="enquiry-next-action-form">
      <div className="grid gap-2 sm:grid-cols-[1fr_15rem]">
        <div>
          <Label className="text-2xs">Next action</Label>
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Call back, send pricing…" maxLength={200}
            data-testid="enquiry-next-action-input" />
        </div>
        <div>
          <Label className="text-2xs">When</Label>
          <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} data-testid="enquiry-next-action-at" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {QUICK.map((q) => (
          <button key={q.days} type="button" onClick={() => setAt(`${isoDate(new Date(Date.now() + q.days * 86400000))}T09:00`)}
            className="rounded-full border border-zinc-300 px-2 py-0.5 text-2xs text-zinc-700 transition-colors hover:border-zinc-900"
            data-testid={`enquiry-next-action-quick-${q.days}`}>{q.label}</button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={onCancel} data-testid="enquiry-next-action-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy || !at} className="bg-zinc-950 text-white" data-testid="enquiry-next-action-save">
            {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}Save</Button>
        </div>
      </div>
    </div>
  );
};

/** Prominent next-action panel at the top of an enquiry, with Reschedule and Done. */
export const NextActionCard = ({ enq, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!OPEN.includes(enq.status || "new")) return null;
  if (editing) return <NextActionForm enq={enq} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged(); }} />;
  const done = async () => {
    setBusy(true);
    try {
      await api.put(`/enquiries/${enq.id}/next-action`, { done: true });
      toast.success("Next action marked done");
      onChanged();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  if (!hasNext(enq)) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-zinc-300 px-3 py-2 text-xs text-zinc-500"
        data-testid="enquiry-next-action-empty">
        <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-4 w-4" />No next action scheduled</span>
        <Button size="sm" variant="outline" className="h-7" onClick={() => setEditing(true)} data-testid="enquiry-next-action-set">Set next action</Button>
      </div>
    );
  }
  const tone = followUpTone(enq.next_follow_up_at);
  return (
    <div className={cn("flex flex-wrap items-center gap-3 rounded-md border p-3", BOX[tone])} data-testid="enquiry-next-action">
      <CalendarClock className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-2xs font-semibold uppercase tracking-wider" data-testid="enquiry-next-action-state">Next action{SUFFIX[tone]}</div>
        <div className="text-sm font-medium" data-testid="enquiry-next-action-text">{enq.next_action || "Follow up"}</div>
        <div className="font-mono-num text-xs" data-testid="enquiry-next-action-date">
          {enq.next_follow_up_at ? fmtDateTime(enq.next_follow_up_at) : "No date set"}</div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="bg-white" onClick={() => setEditing(true)} data-testid="enquiry-next-action-reschedule">
          <CalendarClock className="mr-1 h-3.5 w-3.5" />Reschedule</Button>
        <Button size="sm" onClick={done} disabled={busy} className="bg-zinc-950 text-white" data-testid="enquiry-next-action-done">
          <Check className="mr-1 h-3.5 w-3.5" />Done</Button>
      </div>
    </div>
  );
};
